import { Check } from 'lucide-react';
import { cn } from './cn';

export interface StepDescriptor {
  id: number;
  title: string;
}

export interface StepperProps {
  steps: StepDescriptor[];
  currentStep: number;
  className?: string;
}

export function Stepper({ steps, currentStep, className }: StepperProps) {
  return (
    <div
      className={cn(
        'flex items-start justify-center max-w-md mx-auto px-4 mb-12 md:mb-16',
        className,
      )}
    >
      {steps.flatMap((step, index) => {
        const isCompleted = currentStep > step.id;
        const isCurrent = currentStep === step.id;
        const isLast = index === steps.length - 1;

        const stepContent = (
          <div
            key={`step-${step.id}`}
            className="flex flex-col items-center gap-2 md:gap-3 shrink-0"
          >
            <div
              className={cn(
                'w-8 h-8 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all shadow-md text-sm md:text-base shrink-0 font-bold',
                isCompleted && 'bg-[#6B7A5A] text-white',
                isCurrent && 'bg-[#1A1A1A] text-[#F4D03F] ring-2 md:ring-4 ring-[#1A1A1A]/20',
                !isCompleted && !isCurrent && 'bg-[#E8DFD3] text-[#78665A]',
              )}
            >
              {isCompleted ? (
                <Check className="w-4 h-4 md:w-6 md:h-6" />
              ) : (
                <span>{step.id}</span>
              )}
            </div>
            <span
              className={cn(
                'text-[10px] md:text-xs text-center hidden sm:block',
                isCurrent ? 'text-[#3D3328] font-semibold' : 'text-[#78665A]',
              )}
            >
              {step.title}
            </span>
          </div>
        );

        const connector = !isLast && (
          <div
            key={`connector-${step.id}`}
            className="flex-1 h-8 md:h-12 flex items-center justify-center min-w-[24px] md:min-w-[48px] shrink"
          >
            <div
              className={cn(
                'w-8 md:w-16 h-0.5 md:h-1 rounded-full transition-all shrink-0',
                isCompleted ? 'bg-[#6B7A5A]' : 'bg-[#E8DFD3]',
              )}
            />
          </div>
        );

        return [stepContent, connector].filter(Boolean);
      })}
    </div>
  );
}
